import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Table, Button, Modal, Form, Input, Checkbox, InputNumber, Select, Tag, notification } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationModal from '../molecules/modals/ConfirmationModal';
import StepUpModal from '../molecules/StepUpModal';
import {
  useListTokensQuery,
  useCreateTokenMutation,
  useRevokeTokenMutation,
} from '../../../services/personalAccessTokensApi';

// Same scopes as the backend (PersonalAccessTokenService.PAT_SCOPES).
const SCOPES = [
  { value: 'tasks:read', id: 'PAT_SCOPE_TASKS_READ', text: 'Read tasks, projects, comments and attachments' },
  { value: 'tasks:write', id: 'PAT_SCOPE_TASKS_WRITE', text: 'Create and update tasks, assign people, move tasks, comment' },
  { value: 'files:write', id: 'PAT_SCOPE_FILES_WRITE', text: 'Upload and delete attachments' },
  { value: 'people:read', id: 'PAT_SCOPE_PEOPLE_READ', text: "See people's email addresses" },
];

const formatDate = (value) => (value ? new Date(value).toLocaleString() : null);
const formatDay = (value) => (value ? new Date(value).toLocaleDateString() : null);
// Inside the Settings modal the accent is indigo (global.css paints every
// antd primary button red, which reads as destructive next to Revoke).
const MODAL_ACCENT_BUTTON = { background: '#6366f1', borderColor: '#6366f1', color: '#fff' };

// compact: two columns (everything about a token in one cell, then Revoke)
// and tighter padding, for the Settings modal's Integrations tab.
const PersonalAccessTokensSettings = ({ compact = false }) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const { data, isFetching } = useListTokensQuery();
  const [createToken, { isLoading: isCreating }] = useCreateTokenMutation();
  const [revokeToken, { isLoading: isRevoking }] = useRevokeTokenMutation();

  const tokens = data?.data || [];

  // The user's workspaces (already in the store): the optional restriction
  // picker in the create form and the names shown in the table.
  const workspaces = useSelector((state) => state.workspaceReducer?.getWorkspaces || []);
  const workspaceOptions = [
    { value: '', label: intl.formatMessage({ id: 'PAT_ALL_WORKSPACES', defaultMessage: 'All workspaces' }) },
    ...[].concat(workspaces || []).filter((w) => w?._id).map((w) => ({ value: String(w._id), label: w.title || String(w._id) })),
  ];
  const scopeOptions = SCOPES.map((s) => ({
    value: s.value,
    label: (
      <span>
        <code className="text-xs">{s.value}</code>
        <span className="text-xs text-gray-500"> — {intl.formatMessage({ id: s.id, defaultMessage: s.text })}</span>
      </span>
    ),
  }));
  const workspaceName = (workspaceId) => {
    if (!workspaceId) return null;
    const match = workspaces.find((w) => String(w._id) === String(workspaceId));
    return match?.title || workspaceId;
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState(null); // { token, personalAccessToken } — cleared on close, never persisted elsewhere
  const [revokeTarget, setRevokeTarget] = useState(null); // token row pending revoke confirmation
  // Validated form values waiting for "Confirm it's you" (the backend requires a
  // fresh password / Google / Apple confirmation to mint a token).
  const [pendingCreate, setPendingCreate] = useState(null);
  const accountEmail = useSelector((state) => state.userReducer?.user?.email);

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreatedToken(null);
    setPendingCreate(null);
    form.resetFields();
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const body = {
        label: values.label,
        scopes: values.scopes,
      };
      if (values.expiresInDays) {
        body.expiresInDays = values.expiresInDays;
      }
      // Optional restriction: the token then only works in that workspace.
      if (values.workspaceId) {
        body.workspaceId = values.workspaceId;
      }
      setPendingCreate(body);
    } catch (err) {
      // antd form validation error, already shown inline
    }
  };

  // Called by StepUpModal with { password } or { firebaseIdToken }.
  const createWithStepUp = async (stepUp) => {
    if (!pendingCreate) return { ok: false };
    try {
      const res = await createToken({ ...pendingCreate, stepUp }).unwrap();
      setPendingCreate(null);
      setCreatedToken(res?.data || null);
      return { ok: true };
    } catch (err) {
      const status = err?.status;
      // Step-up problems (wrong password, SSO required, too many attempts,
      // stale sign-in) are shown inside the confirmation dialog for a retry.
      if (status === 400 || status === 401 || status === 403 || status === 429) {
        if (err?.data?.code) return { ok: false, message: err.data.message };
      }
      setPendingCreate(null);
      notification.error({
        message: err?.data?.message || intl.formatMessage({ id: 'PAT_CREATE_FAILED', defaultMessage: 'Failed to create token' }),
        placement: 'bottomRight',
      });
      return { ok: true }; // closes the confirmation dialog; the error is in the toast
    }
  };

  const handleCopyToken = () => {
    if (!createdToken?.token) return;
    navigator.clipboard.writeText(createdToken.token);
    notification.success({
      message: intl.formatMessage({ id: 'PAT_TOKEN_COPIED', defaultMessage: 'Token copied to clipboard' }),
      placement: 'bottomRight',
    });
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    try {
      await revokeToken(revokeTarget._id).unwrap();
    } catch (err) {
      notification.error({
        message: intl.formatMessage({ id: 'PAT_REVOKE_FAILED', defaultMessage: 'Failed to revoke token' }),
        placement: 'bottomRight',
      });
    } finally {
      setRevokeTarget(null);
    }
  };

  const scopeTags = (scopes) => (
    <>
      {(scopes || []).map((scope) => (
        <Tag key={scope} className="mb-0.5">{scope}</Tag>
      ))}
    </>
  );
  const allWorkspacesText = intl.formatMessage({ id: 'PAT_ALL_WORKSPACES', defaultMessage: 'All workspaces' });
  const neverText = intl.formatMessage({ id: 'PAT_NEVER_LOWER', defaultMessage: 'never' });
  // "Workspace · expires 05/01/2027 · last used 10/06/2026" under the label.
  const metaLine = (record) =>
    [
      record.workspaceId ? workspaceName(record.workspaceId) : allWorkspacesText,
      intl.formatMessage(
        { id: 'PAT_EXPIRES_ON', defaultMessage: 'expires {date}' },
        { date: formatDay(record.expiresAt) || neverText }
      ),
      intl.formatMessage(
        { id: 'PAT_LAST_USED_ON', defaultMessage: 'last used {date}' },
        { date: formatDay(record.lastUsedAt) || neverText }
      ),
    ].join(' · ');

  const actionsColumn = {
    title: <FormattedMessage id="PAT_ACTIONS" defaultMessage="Actions" />,
    key: 'actions',
    render: (_, record) =>
      !record.revokedAt && (
        <Button danger size="small" onClick={() => setRevokeTarget(record)}>
          <FormattedMessage id="PAT_REVOKE" defaultMessage="Revoke" />
        </Button>
      ),
  };
  const compactColumns = [
    {
      title: <FormattedMessage id="PAT_TOKEN" defaultMessage="Token" />,
      key: 'token',
      render: (_, record) => (
        <div className="flex flex-col gap-1">
          <span className="text-sm text-gray-900">{record.label}</span>
          <div>{scopeTags(record.scopes)}</div>
          <span className="text-xs text-gray-500">{metaLine(record)}</span>
        </div>
      ),
    },
    { ...actionsColumn, title: null, align: 'right', width: 90 },
  ];
  const allColumns = [
    {
      title: <FormattedMessage id="PAT_LABEL" defaultMessage="Label" />,
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: <FormattedMessage id="PAT_SCOPES" defaultMessage="Scopes" />,
      dataIndex: 'scopes',
      key: 'scopes',
      render: scopeTags,
    },
    {
      title: <FormattedMessage id="PAT_WORKSPACE" defaultMessage="Workspace" />,
      dataIndex: 'workspaceId',
      key: 'workspaceId',
      render: (workspaceId) =>
        workspaceId
          ? workspaceName(workspaceId)
          : <FormattedMessage id="PAT_ALL_WORKSPACES" defaultMessage="All workspaces" />,
    },
    {
      title: <FormattedMessage id="PAT_CREATED" defaultMessage="Created" />,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => formatDate(value) || '—',
    },
    {
      title: <FormattedMessage id="PAT_EXPIRES" defaultMessage="Expires" />,
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (value) => formatDate(value) || <FormattedMessage id="PAT_NEVER" defaultMessage="Never" />,
    },
    {
      title: <FormattedMessage id="PAT_LAST_USED" defaultMessage="Last used" />,
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (value) => formatDate(value) || <FormattedMessage id="PAT_NEVER" defaultMessage="Never" />,
    },
    {
      title: <FormattedMessage id="PAT_STATUS" defaultMessage="Status" />,
      dataIndex: 'revokedAt',
      key: 'status',
      render: (revokedAt) =>
        revokedAt
          ? <Tag color="red"><FormattedMessage id="PAT_REVOKED" defaultMessage="Revoked" /></Tag>
          : <Tag color="green"><FormattedMessage id="PAT_ACTIVE" defaultMessage="Active" /></Tag>,
    },
    actionsColumn,
  ];
  const columns = compact ? compactColumns : allColumns;
  const pad = compact ? 'px-4 py-3' : 'px-6 py-4';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className={`${pad} flex items-center justify-between gap-4 border-b border-gray-100`}>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            <FormattedMessage id="PAT_SECTION_TITLE" defaultMessage="Personal Access Tokens" />
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            <FormattedMessage
              id="PAT_SECTION_DESCRIPTION"
              defaultMessage="Tokens let external tools (like the MCP server) access your account on your behalf."
            />
          </p>
        </div>
        <Button
          type={compact ? 'default' : 'primary'}
          style={compact ? MODAL_ACCENT_BUTTON : undefined}
          className="shrink-0"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <FormattedMessage id="PAT_CREATE_BUTTON" defaultMessage="Create token" />
        </Button>
      </div>

      <div className={pad}>
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={tokens}
          loading={isFetching}
          pagination={false}
          size="small"
          locale={
            compact
              ? { emptyText: <span className="text-xs text-gray-400"><FormattedMessage id="PAT_EMPTY" defaultMessage="No tokens yet." /></span> }
              : undefined
          }
        />
      </div>

      <Modal
        title={
          createdToken
            ? <FormattedMessage id="PAT_TOKEN_CREATED_TITLE" defaultMessage="Token created" />
            : <FormattedMessage id="PAT_CREATE_MODAL_TITLE" defaultMessage="Create Personal Access Token" />
        }
        open={isCreateModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        destroyOnClose
      >
        {createdToken ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-red-600 font-medium">
              <FormattedMessage
                id="PAT_TOKEN_SHOWN_ONCE_WARNING"
                defaultMessage="This is the only time this token will be shown. Copy it now and store it somewhere safe — it cannot be retrieved again."
              />
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={createdToken.token} />
              <Button onClick={handleCopyToken}>
                <FormattedMessage id="PAT_COPY" defaultMessage="Copy" />
              </Button>
            </div>
            <div className="flex justify-end">
              <Button type="primary" onClick={closeCreateModal}>
                <FormattedMessage id="PAT_DONE" defaultMessage="Done" />
              </Button>
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" initialValues={{ scopes: [], workspaceId: '' }}>
            <Form.Item
              name="label"
              label={<FormattedMessage id="PAT_LABEL" defaultMessage="Label" />}
              rules={[{ required: true, message: intl.formatMessage({ id: 'PAT_LABEL_REQUIRED', defaultMessage: 'A label is required' }) }]}
            >
              <Input placeholder={intl.formatMessage({ id: 'PAT_LABEL_PLACEHOLDER', defaultMessage: 'e.g. My laptop MCP client' })} />
            </Form.Item>

            <Form.Item
              name="scopes"
              label={<FormattedMessage id="PAT_SCOPES" defaultMessage="Scopes" />}
              rules={[{
                validator: (_, value) =>
                  value && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(intl.formatMessage({ id: 'PAT_SCOPES_REQUIRED', defaultMessage: 'Select at least one scope' })),
              }]}
            >
              <Checkbox.Group options={scopeOptions} className="flex flex-col gap-1" />
            </Form.Item>

            <Form.Item
              name="workspaceId"
              label={<FormattedMessage id="PAT_WORKSPACE" defaultMessage="Workspace" />}
              extra={intl.formatMessage({
                id: 'PAT_WORKSPACE_HELP',
                defaultMessage: 'Restrict the token to one workspace, or let it work in all of yours.',
              })}
            >
              <Select options={workspaceOptions} />
            </Form.Item>

            <Form.Item
              name="expiresInDays"
              label={<FormattedMessage id="PAT_EXPIRES_IN_DAYS" defaultMessage="Expires in (days)" />}
              extra={intl.formatMessage({ id: 'PAT_EXPIRES_IN_DAYS_HELP', defaultMessage: 'Leave blank to use the default (90 days).' })}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <div className="flex justify-end gap-2">
              <Button onClick={closeCreateModal}>
                <FormattedMessage id="CANCEL" defaultMessage="Cancel" />
              </Button>
              <Button type="primary" loading={isCreating} onClick={handleCreate}>
                <FormattedMessage id="PAT_CREATE_BUTTON" defaultMessage="Create token" />
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      <StepUpModal
        open={!!pendingCreate}
        accountEmail={accountEmail}
        onCancel={() => setPendingCreate(null)}
        onConfirm={createWithStepUp}
        description={
          <FormattedMessage
            id="PAT_STEP_UP_DESCRIPTION"
            defaultMessage="Access tokens can act on your account for months, so confirm it's you before creating one."
          />
        }
      />

      <ConfirmationModal
        isModalOpen={!!revokeTarget}
        title={<FormattedMessage id="PAT_REVOKE_CONFIRMATION_TITLE" defaultMessage="Revoke token?" />}
        description={
          <FormattedMessage
            id="PAT_REVOKE_CONFIRMATION_DESCRIPTION"
            defaultMessage="Are you sure you want to revoke {label}? Anything using this token will stop working immediately."
            values={{ label: revokeTarget?.label }}
          />
        }
        onYesClick={handleRevokeConfirm}
        onCancel={() => setRevokeTarget(null)}
        yesText={isRevoking ? <FormattedMessage id="PAT_REVOKING" defaultMessage="Revoking..." /> : undefined}
        danger
      />
    </div>
  );
};

export default PersonalAccessTokensSettings;
